import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { client, urlFor } from '../lib/sanity';
import { ChevronLeft, Heart, Calendar, MapPin, Quote } from 'lucide-react';
import type { SuccessStory } from '../types/sanity';
import { StoryDate } from '../components/common/StoryDate';

export function StoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = React.useState<SuccessStory | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchStory = async () => {
      try {
        const result = await client.fetch<SuccessStory>(`
          *[_type == "successStory" && _id == $id && !(_id in path("drafts.**"))][0] {
            _id,
            ownerName,
            adoptionDate,
            location,
            quote,
            testimony,
            impactQuote,
            image,
            media,
            wasSpecialCase,
            adoptionType,
            "pet": pet->{
              name,
              mainCategory,
              image,
              description
            },
            "additionalPets": additionalPets[]->{
              name,
              mainCategory,
              image,
              description
            }
          }
        `, { id });

        if (!result) {
          setError('Historia nie została znaleziona');
          setLoading(false);
          return;
        }

        setStory(result);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching story:', err);
        setError('Nie udało się załadować historii');
        setLoading(false);
      }
    };

    if (id) {
      fetchStory();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <p className="text-center text-xl text-accessible-gray-600 dark:text-accessible-gray-300">
          Ładowanie...
        </p>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <p className="text-center text-xl text-accessible-red-600">
          {error || 'Historia nie została znaleziona'}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <Helmet>
        <title>{`Historia ${story.pet?.name} | Fundacja "Znajdki"`}</title>
        <meta name="description" content={story.quote} />
      </Helmet>

      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-accessible-gray-600 hover:text-accessible-gray-800 dark:text-accessible-gray-300 dark:hover:text-white mb-8 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        Powrót
      </button>

      <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black rounded-lg shadow-lg overflow-hidden">
        <div className="relative">
          <div className="aspect-video">
            <img
              src={story.image ? urlFor(story.image).width(1200).height(675).url() : 
                   story.pet?.image ? urlFor(story.pet.image).width(1200).height(675).url() :
                   '/images/animals/fundacja-znajdki-zwierzeta-014.webp'}
              alt={`${story.pet?.name || 'Podopieczny'} w nowym domu z ${story.ownerName}`}
              className="w-full h-full object-cover"
            />
          </div>
          {story.wasSpecialCase && (
            <div className="absolute top-4 right-4 bg-accessible-red-600 text-white px-3 py-1 rounded-full text-sm">
              Wyjątkowy przypadek
            </div>
          )}
        </div>

        <div className="p-8">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-accessible-red-600" />
            <span className="body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white no-margin">
              {story.adoptionType === 'virtual' ? 'Wirtualny opiekun' : 'Nowy dom'}: {story.ownerName}
            </span>
          </div>

          <h1 className="text-3xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-6">
            Historia {story.pet?.name}
            {story.additionalPets && story.additionalPets.length > 0 && (
              <> i {story.additionalPets.map(pet => pet.name).join(', ')}</>
            )}
          </h1>

          <div className="flex flex-wrap items-center gap-4 mb-8 body-small text-accessible-gray-700 dark:text-white/90 high-contrast:text-white no-margin">
            <StoryDate date={new Date(story.adoptionDate).toLocaleDateString('pl-PL', {
              year: 'numeric',
              month: 'long'
            })} />
            
            {story.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{story.location}</span>
              </div>
            )}
          </div>

          <Quote className="w-12 h-12 text-accessible-red-600 mb-6" />
          <blockquote className="quote-text mb-8">
            {story.quote}
          </blockquote>

          {story.testimony && (
            <div className="prose prose-lg dark:prose-invert max-w-none mb-8">
              <p className="body-large">
                {story.testimony}
              </p>
            </div>
          )}

          {story.impactQuote && (
            <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg mb-8">
              <h2 className="heading-4 mb-4">
                Co się zmieniło po {story.adoptionType === 'virtual' ? 'rozpoczęciu wirtualnej adopcji' : 'adopcji'}?
              </h2>
              <p className="body-medium italic">
                "{story.impactQuote}"
              </p>
            </div>
          )}

          {story.media && story.media.length > 0 && (
            <div className="mt-8">
              <h2 className="heading-4 mb-4">
                Galeria
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {story.media.map((media, index) => (
                  'asset' in media ? (
                    <img
                      key={index}
                      src={urlFor(media).width(300).height(300).url()}
                      alt={`Zdjęcie ${index + 1}`}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                  ) : null
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}